<template>
  <div>
    <ontos-kanban-board ref="board"
                        :key="`${context.system}/${context.idea}`"
                        :context="context"
                        :workspace="workspace"
                        @open-instance="onOpenInstance"
                        @no-dimensions="goToList"
                        @showMessage="$emit('showMessage', $event)"
                        @showErrorMessage="$emit('showErrorMessage', $event)">
      <!-- Só no xs: acima disso as visões vivem na faixa do caminho, no App.vue -->
      <template v-if="$vuetify.display.xs" #tabs>
        <ontos-collection-view-tabs :idea="idea" :ideas="workspace.ideas"/>
      </template>
    </ontos-kanban-board>

    <ontos-view-instance-dialog v-if="instanceOnView"
                                :context="instanceOnView"
                                :preview="previewOnView"
                                :workspace="workspace"
                                @changed="board.refreshInstance($event)"
                                @deleted="board.removeInstance($event)"
                                @closed="instanceOnView = null"
                                @showMessage="$emit('showMessage', $event)"
                                @showErrorMessage="$emit('showErrorMessage', $event)"/>
  </div>
</template>

<script lang="ts">
import {Component, toNative} from 'vue-facing-decorator'
import IdeaLevelRouteComponent from "@/components/shared/IdeaLevelRouteComponent";
import CollectionViewTabs from "@/components/live/instance/CollectionViewTabs.vue";
import KanbanBoard from "@/components/live/instance/KanbanBoard.vue";
import ViewInstanceDialog from "@/components/live/instance/ViewInstanceDialog.vue";
import {InstanceContext} from "@/types/InstanceContext";
import {Preview} from "@/utils/instancePreview";

/**
 * Página da rota kanban. O tabuleiro em si vive no KanbanBoard, que também é usado
 * pelo modal aberto de dentro de uma instância — aqui só ficam as decisões que são
 * da página: de onde vem o contexto, para onde navegar e o que fazer no clique.
 */
@Component({
  components: {
    'ontos-kanban-board': KanbanBoard,
    'ontos-view-instance-dialog': ViewInstanceDialog,
    'ontos-collection-view-tabs': CollectionViewTabs
  },
})
class Kanban extends IdeaLevelRouteComponent {
  instanceOnView: InstanceContext | null = null
  /** O que o quadro já tem do item aberto, para a ficha mostrar já. */
  previewOnView: Preview | null = null

  onOpenInstance(context: InstanceContext, preview: Preview | null) {
    this.instanceOnView = context
    this.previewOnView = preview
  }

  /**
   * A coleção na chave porque o router reaproveita o componente quando só os params mudam — sem
   * isso, ir de um Kanban a outro mostraria o quadro anterior.
   *
   * Havia também um contador aqui, incrementado a cada mudança feita no modal para **remontar**
   * o quadro e recarregá-lo. Saiu: remontar relê a coleção inteira para atualizar um card,
   * refotografa as raias — e uma que ficou vazia some — e perde a rolagem. O que mudou é um
   * item, e é só nele que se mexe, pelos comandos do quadro.
   */
  declare $refs: {board: InstanceType<typeof KanbanBoard>}

  /** O tabuleiro, para mandar os comandos. Getter porque o template não enxerga `$refs` direto. */
  get board() {
    return this.$refs.board
  }

  get context(): InstanceContext {
    const {system, idea} = this.$route.params
    return {system, idea} as InstanceContext
  }

  //Coleção sem fluxo não tem o que mostrar num quadro
  async goToList() {
    await this.$router.push({name: 'list-instances', params: this.$route.params})
  }
}

export default toNative(Kanban)
</script>
